export const hasSscc = (value: unknown): boolean => String(value ?? '').trim().length > 0;

/**
 * The SSCC `unit_identifier` values a new transport unit can reuse: every identifier the session has
 * seen (`existingSsccs`) minus the ones still held by surviving units (`ssccsInUse`), deduped.
 * Identifiers are a finite resource (drawn from a company's GS1 pool), so a deleted unit's number
 * becomes available again on its own — reuse falls out of this set difference with no separate
 * bookkeeping.
 */
export const getReusableIdentifiers = (
  ssccsInUse: Array<unknown> = [],
  existingSsccs: Array<unknown> = [],
): Array<string> => {
  const inUse = new Set(ssccsInUse.filter(hasSscc).map((value) => String(value)));
  return Array.from(
    new Set(existingSsccs.filter((value) => hasSscc(value) && !inUse.has(String(value))).map((value) => String(value))),
  );
};

/**
 * Hands out `unit_identifier` values for transport units that don't have one yet.
 *
 * Reuse stays on the client: a reusable identifier no longer in use (see `getReusableIdentifiers`)
 * is handed out first. Only once none are left do we draw from `generatedSsccs` — the fresh SSCC
 * numbers the backend just issued (POST /v2/capabilities) for exactly the units still missing one.
 * Running out of both is a programming error (the caller sizes the request off the same reuse
 * derivation), so it throws rather than emitting a unit without an identifier.
 */
export const createSsccGenerator = (
  ssccsInUse: Array<unknown> = [],
  existingSsccs: Array<unknown> = [],
  generatedSsccs: Array<string> = [],
) => {
  const reusable = getReusableIdentifiers(ssccsInUse, existingSsccs);
  const generated = [...generatedSsccs];
  return (): string => {
    const next = reusable.shift() ?? generated.shift();
    if (next === undefined) {
      throw new Error('No unit identifier available: reuse pool and generated identifiers are both exhausted');
    }
    return next;
  };
};
